"use client"

import { createContext, useContext, useState, useEffect } from 'react'

const ThemeContext = createContext()

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// 라이트/다크 테마 색상 정의
export const themes = {
  light: {
    // 기본 배경
    backgroundColor: '#f5f5f5',
    cardBackground: '#ffffff',
    
    // 텍스트
    textPrimary: '#111827',
    textSecondary: '#6b7280',
    textMuted: '#9ca3af',
    
    // 테두리
    border: '#e5e7eb',
    borderLight: '#f3f4f6',
    
    // 버튼
    buttonPrimary: '#2563eb',
    buttonPrimaryHover: '#1d4ed8',
    buttonSecondary: '#f3f4f6',
    buttonSecondaryHover: '#e5e7eb',
    
    // 입력 필드
    inputBackground: '#ffffff',
    inputBorder: '#e5e7eb',
    inputFocus: '#2563eb',
    
    // 네비게이션
    navBackground: '#ffffff',
    navShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    
    // 사이드바
    sidebarBackground: '#ffffff',
    
    // 호버
    hoverBackground: '#f9fafb',
    
    // 그림자
    shadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    shadowHover: '0 4px 12px rgba(0, 0, 0, 0.15)',
    
    // 특수 색상
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
  },
  dark: {
    // 기본 배경
    backgroundColor: '#0f1419',
    cardBackground: '#1f2937',
    
    // 텍스트
    textPrimary: '#f9fafb',
    textSecondary: '#d1d5db',
    textMuted: '#9ca3af',
    
    // 테두리
    border: '#374151',
    borderLight: '#4b5563',
    
    // 버튼
    buttonPrimary: '#3b82f6',
    buttonPrimaryHover: '#2563eb',
    buttonSecondary: '#374151',
    buttonSecondaryHover: '#4b5563',
    
    // 입력 필드
    inputBackground: '#374151',
    inputBorder: '#4b5563',
    inputFocus: '#3b82f6',
    
    // 네비게이션
    navBackground: '#1f2937',
    navShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
    
    // 사이드바
    sidebarBackground: '#1f2937',
    
    // 호버
    hoverBackground: '#374151',
    
    // 그림자
    shadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
    shadowHover: '0 4px 12px rgba(0, 0, 0, 0.4)',
    
    // 특수 색상
    success: '#059669',
    warning: '#d97706',
    error: '#dc2626',
    info: '#2563eb',
  }
}

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false)

  // 초기화 시 로컬 스토리지에서 테마 설정 불러오기
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme')
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark')
    } else {
      setIsDarkMode(systemPrefersDark)
    }
  }, [])

  // 테마 변경 시 로컬 스토리지에 저장
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light')
    
    // HTML 엘리먼트에 클래스 추가/제거
    if (isDarkMode) {
      document.documentElement.classList.add('dark')
      document.documentElement.classList.remove('light')
    } else {
      document.documentElement.classList.add('light')
      document.documentElement.classList.remove('dark')
    }
  }, [isDarkMode])

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev)
  }

  const currentTheme = isDarkMode ? themes.dark : themes.light

  const value = {
    isDarkMode,
    toggleTheme,
    theme: currentTheme,
    themes
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}